// -----------------------------------------------------------------------------
// 5) src/components/EngagementSummary.jsx
// -----------------------------------------------------------------------------
import React from "react";
import { api } from "../lib/api";
import StarRating from "./StarRating";
import { FavoriteButton } from "./FavoriteButton";

export function EngagementSummary({
  target,
  objectId,
  readOnly = false,
  showCounts = true,
  initialSummary = null,
  trailingAction = null,
}) {
  const [summary, setSummary] = React.useState(() => ({
    avg: Number(initialSummary?.avg ?? initialSummary?.avg_rating ?? 0) || 0,
    count: Number(
      initialSummary?.count
      ?? initialSummary?.ratings_count
      ?? 0
    ) || 0,
    favorites_count: Number(
      initialSummary?.favorites_count
      ?? initialSummary?.favorited_count
      ?? 0
    ) || 0,
    user_rating: initialSummary?.user_rating ?? null,
  }));
  const [fav, setFav] = React.useState(() => Boolean(initialSummary?.favorited));
  const blurredTextSx = {
    display: 'inline-block',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(14px)',
    borderRadius: 1.5,
    px: 1.5,
    py: 0.75,
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    color: '#111827'
  };

  const load = React.useCallback(async () => {
    try {
      const s = await api.getJson(`/engagement/ratings/summary/?target=${target}&object_id=${objectId}`);
      setSummary({
        avg: s?.avg || 0,
        count: s?.count || 0,
        favorites_count: Number(s?.favorites_count ?? s?.favorited_count ?? 0) || 0,
        user_rating: s?.user_rating ?? null,
      });
      if (typeof s?.favorited === "boolean") {
        setFav(s.favorited);
      }
      // Optional: query whether user favorited (if you expose such endpoint later). For now, leave as false and rely on initial prop in FavoriteButton.
    } catch (e) { console.warn("summary load failed", e); }
  }, [objectId, target]);

  React.useEffect(() => {
    if (initialSummary) return;
    load();
  }, [initialSummary, load]);

  React.useEffect(() => {
    if (!initialSummary) return;
    setSummary({
      avg: Number(initialSummary?.avg ?? initialSummary?.avg_rating ?? 0) || 0,
      count: Number(
        initialSummary?.count
        ?? initialSummary?.ratings_count
        ?? 0
      ) || 0,
      favorites_count: Number(
        initialSummary?.favorites_count
        ?? initialSummary?.favorited_count
        ?? 0
      ) || 0,
      user_rating: initialSummary?.user_rating ?? null,
    });
    setFav(Boolean(initialSummary?.favorited));
  }, [initialSummary]);

  const handleFavoriteChange = React.useCallback((nextFav) => {
    setFav(nextFav);
    setSummary((prev) => ({
      ...prev,
      favorites_count: Math.max(0, Number(prev.favorites_count || 0) + (nextFav ? 1 : -1)),
    }));
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", flexWrap: "wrap" }} sx={blurredTextSx}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <StarRating
          target={target}
          objectId={objectId}
          value={Math.round(summary.avg)}
          onChange={() => load()}
          readOnly={readOnly}
        />
        {showCounts && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>{summary.avg?.toFixed?.(2) || "0.00"} ({summary.count})</div>
        )}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FavoriteButton target={target} objectId={objectId} initial={fav} onChange={handleFavoriteChange} />
          <div style={{ fontSize: 13, color: "#6b7280" }}>({summary.favorites_count || 0})</div>
        </div>
      </div>
      {trailingAction ? (
        <div style={{ display: "inline-flex", alignItems: "center", marginLeft: 2 }}>
          {trailingAction}
        </div>
      ) : null}
    </div>
  );
}
